import { lumi } from '@/lib/lumi'
import { useState } from 'react'

export interface Review {
  _id: string
  bottleId?: string
  bottleName: string
  source: string
  rating: number
  sentiment: string
  quote: string
  reviewerName: string
  status: 'pending' | 'approved' | 'rejected'
  creator: string
  creatorEmail?: string
  createdAt: string
  updatedAt: string
}

export const useReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchReviews = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.reviews.list({
        sort: { createdAt: -1 },
        ...options
      })
      setReviews(list)
      setTotal(total)
      console.log('Reviews fetched:', list)
    } catch (error) {
      console.error('Failed to fetch reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  // Public submission entry point: any visitor can submit a review.
  // New submissions always start as "pending" and require moderation before
  // they appear in public-facing approved review lists.
  const submitReview = async (reviewData: {
    bottleId?: string
    bottleName: string
    source: string
    rating: number
    sentiment: string
    quote: string
    reviewerName: string
  }) => {
    try {
      const now = new Date().toISOString()
      const currentUser = lumi.auth.user
      const newReview = await lumi.entities.reviews.create({
        ...reviewData,
        status: 'pending',
        creator: currentUser?.userId || 'anonymous',
        creatorEmail: currentUser?.email || '',
        createdAt: now,
        updatedAt: now
      })
      console.log('Review submitted for moderation:', newReview)
      return newReview
    } catch (error) {
      console.error('Failed to submit review:', error)
      throw error
    }
  }

  const createReview = async (reviewData: Omit<Review, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const newReview = await lumi.entities.reviews.create({
        ...reviewData,
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      })
      console.log('Review created:', newReview)
      await fetchReviews()
      return newReview
    } catch (error) {
      console.error('Failed to create review:', error)
      throw error
    }
  }

  const updateReview = async (id: string, updates: Partial<Review>) => {
    if (typeof id !== 'string') {
      throw new Error('ID must be a string')
    }

    try {
      const updatedReview = await lumi.entities.reviews.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Review updated:', updatedReview)
      await fetchReviews()
      return updatedReview
    } catch (error) {
      console.error('Failed to update review:', error)
      throw error
    }
  }

  // Moderation actions: approve or reject a pending review.
  const approveReview = async (id: string) => updateReview(id, { status: 'approved' })
  const rejectReview = async (id: string) => updateReview(id, { status: 'rejected' })

  const deleteReview = async (id: string) => {
    if (typeof id !== 'string' || id === '[object Object]') {
      throw new Error('Invalid ID: must be a string, not an object')
    }

    try {
      await lumi.entities.reviews.delete(id)
      console.log('Review deleted:', id)
      await fetchReviews()
    } catch (error) {
      console.error('Failed to delete review:', error)
      throw error
    }
  }

  return {
    reviews,
    total,
    loading,
    fetchReviews,
    submitReview,
    createReview,
    updateReview,
    approveReview,
    rejectReview,
    deleteReview
  }
}
